// File: src/hooks/useImageGeneration.tsx
import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
    ImageGeneration,
    Product,
    AIService,
    PromptTemplate
} from '../types';

import {
    startPromptGeneration,
    startImageGeneration,
    getGenerationStatus,
    getGenerationsForProduct,
} from '../services/api';

interface UseImageGenerationProps {
    selectedProductId: number | '';
    selectedAIServiceCode: string;
    selectedImageServiceCode: string;
    selectedPromptTemplateId: number | '';
    numImages: number;
    additionalInstructions: string;
    selectedReferenceImageUrl: string | null;
    allAiServices?: AIService[];
    promptTemplates?: PromptTemplate[];
}

export const useImageGeneration = ({
                                       selectedProductId,
                                       selectedAIServiceCode,
                                       selectedImageServiceCode,
                                       selectedPromptTemplateId,
                                       numImages,
                                       additionalInstructions,
                                       selectedReferenceImageUrl,
                                       allAiServices,
                                       promptTemplates,
                                   }: UseImageGenerationProps) => {
    const { t } = useTranslation();
    const [generationId, setGenerationId] = useState<number | null>(null);
    const [generationStep, setGenerationStep] = useState<
        'idle' | 'prompt_generating' | 'prompts_ready' | 'image_generating' | 'completed' | 'failed' | 'prompt_failed' | 'image_failed'
    >('idle');
    const [generatedPrompts, setGeneratedPrompts] = useState<string[]>([]);
    const [generatedImages, setGeneratedImages] = useState<{ url: string; status: string; error?: string | null }[]>([]);
    const [error, setError] = useState<string | null>(null);
    // ✅ New states for raw AI data
    const [aiRawPrompt, setAiRawPrompt] = useState<string | null>(null);
    const [aiRawResponse, setAiRawResponse] = useState<string | null>(null);

    // ✅ Функция для получения переведённого статуса
    const getStatusText = (status: typeof generationStep) => {
        return t(`image_generation.status.${status}`);
    };

    const selectedPromptTemplate = useMemo(() => {
        return promptTemplates?.find(t => t.id === selectedPromptTemplateId);
    }, [promptTemplates, selectedPromptTemplateId]);

    const selectedAIService = useMemo(() => {
        return allAiServices?.find(s => s.code === selectedAIServiceCode);
    }, [allAiServices, selectedAIServiceCode]);

    const selectedImageService = useMemo(() => {
        return allAiServices?.find(s => s.code === selectedImageServiceCode);
    }, [allAiServices, selectedImageServiceCode]);

    const promptGenMutation = useMutation({
        mutationFn: () => {
            if (!selectedProductId || !selectedReferenceImageUrl) {
                throw new Error(t('image_generation.product_and_image_required'));
            }
            return startPromptGeneration(selectedProductId, {
                prompt_template_id: selectedPromptTemplateId as number,
                ai_service_used: selectedAIServiceCode,
                image_ai_service_used: selectedImageServiceCode,
                language: 'ru',
                num_images: numImages,
                extra_instructions: additionalInstructions,
                reference_image_url: selectedReferenceImageUrl,
            });
        },
        onSuccess: (data: { id: number; status: string }) => {
            setGenerationId(data.id);
            setGenerationStep('prompt_generating');
            setError(null);
            // Clear old raw AI data
            setAiRawPrompt(null);
            setAiRawResponse(null);
        },
        onError: (e: any) => {
            setError(e.message || t('image_generation.prompt_generation_failed'));
            setGenerationStep('prompt_failed');
        },
    });

    const generationStatusQuery = useQuery<ImageGeneration>({
        queryKey: ['generationStatus', generationId],
        queryFn: () => getGenerationStatus(selectedProductId as number, generationId as number),
        enabled: !!generationId && generationStep !== 'idle' && generationStep !== 'completed' && generationStep !== 'failed',
        refetchInterval: 3000,
    });

    useEffect(() => {
        const data = generationStatusQuery.data;
        if (!data) return;

        // Update raw AI data
        setAiRawPrompt(data.ai_raw_prompt || null);
        setAiRawResponse(data.ai_raw_response || null);

        switch (data.status) {
            case 'prompt_generating':
            case 'pending':
                setGenerationStep('prompt_generating');
                break;
            case 'prompts_ready':
                setGenerationStep('prompts_ready');
                setGeneratedPrompts(data.prompt_generation_result_parsed ?? []);
                break;
            case 'prompt_failed':  // new
                setGenerationStep('prompt_failed');
                setError(data.error_message ?? t('image_generation.prompt_generation_failed'));
                break;
            case 'image_generating':
                setGenerationStep('image_generating');
                setGeneratedImages(
                    (data.image_requests || []).map(req => ({
                        url: req.generated_image_url ?? '',
                        status: req.generation_status,
                        error: req.error ?? undefined,
                    }))
                );
                break;
            case 'image_failed':  // new
                setGenerationStep('image_failed');
                setGeneratedImages(
                    (data.image_requests || []).map(req => ({
                        url: req.generated_image_url ?? '',
                        status: req.generation_status,
                        error: req.error ?? undefined,
                    }))
                );
                setError(data.error_message ?? t('image_generation.image_generation_failed'));
                break;
            case 'completed':
                setGenerationStep('completed');
                setGeneratedImages(
                    (data.image_requests || []).map(req => ({
                        url: req.generated_image_url ?? '',
                        status: req.generation_status,
                        error: req.error ?? undefined,
                    }))
                );
                break;
            case 'failed': // fallback or legacy errors
                setGenerationStep('failed');
                setError(data.error_message ?? t('image_generation.generation_failed'));
                break;
        }
    }, [generationStatusQuery.data, t]);

    const imageGenMutation = useMutation({
        mutationFn: () => startImageGeneration(selectedProductId as number, generationId as number),
        onSuccess: () => {
            setGenerationStep('image_generating');
            setError(null);
        },
        onError: (e: any) => {
            setError(e.message || t('image_generation.image_generation_start_failed'));
            setGenerationStep('image_failed');
        },
    });

    const resetGeneration = () => {
        setGenerationId(null);
        setGenerationStep('idle');
        setGeneratedPrompts([]);
        setGeneratedImages([]);
        setError(null);
        // Clear raw AI data on reset
        setAiRawPrompt(null);
        setAiRawResponse(null);
    };

    const handleLoadGeneration = (gen: ImageGeneration) => {
        setGenerationId(gen.id);
        setGenerationStep(gen.status as any);
        setGeneratedPrompts(gen.prompt_generation_result_parsed || []);
        setGeneratedImages(
            (gen.image_requests || []).map(req => ({
                url: req.generated_image_url ?? '',
                status: req.generation_status,
                error: req.error,
            }))
        );
        // Load raw AI data from history
        setAiRawPrompt(gen.ai_raw_prompt || null);
        setAiRawResponse(gen.ai_raw_response || null);
    };

    const canStartPromptGen =
        (generationStep === 'idle' || generationStep === 'prompt_failed') &&
        selectedProductId !== '' &&
        selectedAIServiceCode !== '' &&
        selectedImageServiceCode !== '' &&
        selectedPromptTemplateId !== '' &&
        selectedReferenceImageUrl !== null;

    const canStartImageGen =
        (generationStep === 'prompts_ready' || generationStep === 'image_failed') &&
        generatedPrompts.length > 0 &&
        generationId !== null;

    return {
        generationId,
        generationStep,
        generatedPrompts,
        generatedImages,
        error,
        selectedPromptTemplate,
        selectedAIService,
        selectedImageService,
        promptGenMutation,
        imageGenMutation,
        resetGeneration,
        handleLoadGeneration,
        canStartPromptGen,
        canStartImageGen,
        aiRawPrompt,
        aiRawResponse,
        getStatusText, // ✅ Экспортируем функцию для перевода статусов
    };
};
